// Phase 10B "AutoMind 2050" — SMS Automation page.
//
// Same data model as Phase 8B.1; just restyled into the new shell:
//  - PageHeader (eyebrow + breadcrumb + title)
//  - TabBar with five sections (Overview / Settings / Recent /
//    Inbound / Templates)
//  - Existing API calls, role gates, and effects untouched.
import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';

import { PageHeader } from '../../components/ui/page-header';
import { TabBar } from '../../components/ui/tab-bar';
import { EmptyState } from '../../components/ui/empty-state';
import { DataCard } from '../../components/ui/data-card';

import { dealersApi } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import SummaryCards from './SummaryCards';
import AutomationControls from './AutomationControls';
import EvaluationsTable from './EvaluationsTable';
import { TEMPLATE_DEFINITIONS } from './constants';

// Phase Audit Fix C: "Inbound replies" tab removed — it only ever
// rendered an empty-state placeholder because no inbound-thread
// surface is wired. Re-add when the conversation viewer ships.
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'settings', label: 'Settings' },
  { id: 'activity', label: 'Recent activity' },
  { id: 'templates', label: 'Templates' },
];

export default function SmsAutomation() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin';
  const isSalesperson = user?.role === 'salesperson';

  // For non-admins the dealer is fixed. Admins can pick from a dropdown.
  const [dealerId, setDealerId] = useState(
    isPlatformAdmin ? null : user?.dealer_id || null
  );
  const [adminDealers, setAdminDealers] = useState(null); // null = not yet attempted
  const [dealer, setDealer] = useState(null);
  const [dealerLoading, setDealerLoading] = useState(false);
  const [dealerError, setDealerError] = useState(null);
  const [tab, setTab] = useState('overview');

  // Lazy-load the admin dealer list on mount so we can populate the picker.
  useEffect(() => {
    if (!isPlatformAdmin) return;
    let cancelled = false;
    dealersApi
      .list()
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res) ? res : res?.items || [];
        setAdminDealers(items);
        if (!dealerId && items.length > 0) {
          setDealerId(items[0].id || items[0]._id);
        }
      })
      .catch(() => {
        if (!cancelled) setAdminDealers([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [isPlatformAdmin]);

  const fetchDealer = useCallback(async () => {
    if (!dealerId) {
      setDealer(null);
      return;
    }
    setDealerLoading(true);
    setDealerError(null);
    try {
      const d = await dealersApi.get(dealerId);
      setDealer(d);
    } catch (err) {
      setDealerError(err);
      setDealer(null);
    } finally {
      setDealerLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchDealer();
  }, [fetchDealer]);

  // Dealer dropdown for admins — sits in the header actions slot.
  const dealerPicker =
    isPlatformAdmin && adminDealers && adminDealers.length > 0 ? (
      <select
        className="h-9 text-sm rounded-md border border-ink-200 dark:border-ink-800 bg-white/70 dark:bg-ink-900/40 px-2 focus-2050"
        value={dealerId || ''}
        onChange={(e) => setDealerId(e.target.value || null)}
        data-testid="sms-dealer-picker"
      >
        {adminDealers.map((d) => {
          const id = d.id || d._id;
          return (
            <option key={id} value={id}>
              {d.name || d.dealer_name || id}
            </option>
          );
        })}
      </select>
    ) : null;

  const header = (
    <PageHeader
      eyebrow="Outbound"
      breadcrumb={[{ label: 'SMS' }]}
      title="SMS Automation"
      description="Automation rules and recent decisions for outbound customer SMS."
      actions={dealerPicker}
    />
  );

  // Empty state: admin without a selectable dealer.
  if (
    isPlatformAdmin &&
    (!dealerId || (adminDealers && adminDealers.length === 0))
  ) {
    return (
      <div className="space-y-6 max-w-[1400px] mx-auto">
        {header}
        <EmptyState
          icon={MessageSquare}
          title="Pick a dealer"
          description="Choose a dealer to view its SMS automation settings and recent decisions."
        />
      </div>
    );
  }

  // Empty state: non-admin user without a dealer assignment.
  if (!isPlatformAdmin && !dealerId) {
    return (
      <div className="space-y-6 max-w-[1400px] mx-auto">
        {header}
        <EmptyState
          icon={MessageSquare}
          title="No dealer assigned"
          description="Your account isn't linked to a dealer yet. Ask an admin to assign you."
        />
      </div>
    );
  }

  const smsAutomation = dealer?.config?.sms_automation;

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto">
      {header}

      <TabBar value={tab} onChange={setTab} items={TABS} />

      {tab === 'overview' && (
        <div className="space-y-6">
          <SummaryCards dealerId={dealerId} />
          <AutomationControls
            dealerId={dealerId}
            sms_automation={smsAutomation}
            onUpdated={fetchDealer}
            readOnly={isSalesperson}
            loading={dealerLoading && !dealer}
            compact
          />
        </div>
      )}

      {tab === 'settings' && (
        <div className="space-y-6">
          <AutomationControls
            dealerId={dealerId}
            sms_automation={smsAutomation}
            onUpdated={fetchDealer}
            readOnly={isSalesperson}
            loading={dealerLoading && !dealer}
          />
        </div>
      )}

      {tab === 'activity' && (
        <div className="space-y-6">
          <EvaluationsTable dealerId={dealerId} />
        </div>
      )}

      {tab === 'templates' && <TemplatesPanel />}

      {dealerError && !dealerLoading && (
        <div className="text-xs text-ink-500">
          Dealer settings could not be loaded; controls reflect defaults.
        </div>
      )}
    </div>
  );
}

// ---- Templates panel -------------------------------------------------------
// Read-only reference of the templates that ship with the service. Bodies
// are mirrored from `backend/services/sms_followup/templates.py` — they're
// hard-coded server-side too (Phase 8A constraint).
function TemplatesPanel() {
  return (
    <div className="space-y-4">
      <DataCard
        title="Message templates"
        description="Read-only reference for the SMS bodies the automation will send. All templates fit a single 160-character segment."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TEMPLATE_DEFINITIONS.map((tmpl) => (
            <div
              key={tmpl.id}
              className="rounded-xl border border-ink-200/70 dark:border-ink-800/70 bg-white/40 dark:bg-ink-900/40 p-4"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-mono text-xs text-ink-700 dark:text-ink-200">
                  {tmpl.id}
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">
                  {tmpl.touch === 2 ? 'Second touch' : 'First touch'}
                </div>
              </div>
              <div className="mt-3 text-sm leading-relaxed text-ink-900 dark:text-ink-50">
                {tmpl.body}
              </div>
              <div className="mt-3 text-xs text-ink-500">{tmpl.description}</div>
            </div>
          ))}
        </div>
      </DataCard>
    </div>
  );
}
